/**
 * Activity Logging Service
 *
 * __BOARD_ACTIVITY__ (2026-10-18) The old writer inserted {user_id, entity_type, entity_id, old_values, new_values,
 * notes} into dispatcher_activity_log, whose real columns are dispatcher_user_id, action_type, target_table,
 * related_load_id, related_customer_id, old_value, new_value, details, timestamp (default now). Every insert failed
 * and nothing said so: of the table's 125 rows (2026-04-05 .. 2026-10-15) not one came from the board.
 *
 * Now:
 *  - a JOB's activity (status move, driver change, Route Builder plan, edit) goes to the hub, POST /api/board/activity
 *    with the signed-in person's token (hubDrafts' pattern). The hub writes it to the audit log under the job's id,
 *    with the person and the time - so it is in the hub's Audit Log and in the job's history (Archived jobs).
 *  - anything else (sign-in / out, customers, drivers, trucks, settings) goes to dispatcher_activity_log with its real
 *    columns, and a refused insert is reported in the console instead of vanishing.
 *  - the readers (the owner's Dispatcher Activity window) read the real columns and hand back the old shape.
 * Never throws: logging must not break the change it records.
 */

import { supabase } from '../supabaseClient';
import { HUB } from './hubDrafts';

const LOAD_ACTIONS = ['status_changed', 'driver_changed', 'route_planned', 'load_edited', 'stops_changed', 'load_created',
  'load_deleted', 'truck_changed'];
const short = (v) => (v === null || v === undefined ? null : String(typeof v === 'object' ? JSON.stringify(v) : v).slice(0, 300));

/** The body the hub gets for one job's activity (pure; sim/activity.test.mjs). */
export const loadActivityBody = (loadId, action, field, oldValue, newValue, note) => {
  if (!loadId || !LOAD_ACTIONS.includes(action)) return null;
  const b = { load_id: String(loadId), action };
  if (field) b.field = String(field);
  if (oldValue !== undefined && oldValue !== null && oldValue !== '') b.old = short(oldValue);
  if (newValue !== undefined && newValue !== null && newValue !== '') b.new = short(newValue);
  if (note) b.note = short(note);
  return b;
};

/** One job's activity -> the hub's audit log (the job's history). */
export const logLoadActivity = async (loadId, action, field = null, oldValue = null, newValue = null, note = null, fetchImpl = null) => {
  const body = loadActivityBody(loadId, action, field, oldValue, newValue, note);
  if (!body) return false;
  try {
    const { data } = await supabase.auth.getSession();
    const t = data && data.session && data.session.access_token;
    if (!t) return false;
    const r = await (fetchImpl || fetch)(HUB + '/api/board/activity', {
      method: 'POST', headers: { Authorization: 'Bearer ' + t, 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (!r.ok) { console.warn('Activity not recorded (hub answered ' + r.status + '):', action); return false; }
    return true;
  } catch (err) {
    console.warn('Activity not recorded:', action, err && err.message);
    return false;
  }
};

const EDIT_LABELS = {
  customer_id: 'customer', truck_id: 'truck', commodity: 'cargo', weight_kg: 'weight', length_cm: 'length', width_cm: 'width',
  height_cm: 'height', booker_name: 'point of contact', estimated_duration_hours: 'duration', scheduled_date: 'job date',
  delivery_date: 'delivery date', notes: 'notes',
  truck_required: 'truck required',   // __OPEN_DECK__
};
/** The fields an Edit changed, in words (pure; the driver is left to the route reconcile's own entry). */
export const editedFields = (before, form) => Object.keys(EDIT_LABELS).filter((k) => {
  const a = before ? before[k] : null;
  const b = form ? form[k] : null;
  const n = (v) => (v === null || v === undefined ? '' : String(v).trim());
  if (/_cm$|weight_kg|estimated_duration_hours/.test(k)) return (parseFloat(n(a)) || 0) !== (parseFloat(n(b)) || 0);
  return n(a) !== n(b);
}).map((k) => EDIT_LABELS[k]);

/** The dispatcher_activity_log row for anything that is not one job (pure). */
export const activityRow = (userId, actionType, entityType, entityId, oldValues, newValues, notes) => {
  const row = { dispatcher_user_id: userId, action_type: String(actionType || '').slice(0, 60) };
  if (entityType) row.target_table = String(entityType);
  if (entityId && entityType === 'load') row.related_load_id = entityId;
  if (entityId && entityType === 'customer') row.related_customer_id = entityId;
  if (oldValues !== null && oldValues !== undefined) row.old_value = oldValues;
  if (newValues !== null && newValues !== undefined) row.new_value = newValues;
  const details = {};
  if (notes) details.notes = String(notes);
  if (entityId && entityType !== 'load' && entityType !== 'customer') details.entity_id = entityId;
  if (Object.keys(details).length) row.details = details;
  return row;
};

/**
 * Log an activity. A job's ('load') goes to the hub; the rest to dispatcher_activity_log.
 */
export const logActivity = async (
  actionType,
  entityType = null,
  entityId = null,
  oldValues = null,
  newValues = null,
  notes = null
) => {
  try {
    if (entityType === 'load' && entityId && LOAD_ACTIONS.includes(actionType)) {
      return await logLoadActivity(entityId, actionType, null, oldValues, newValues, notes);
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { error } = await supabase.from('dispatcher_activity_log')
      .insert(activityRow(user.id, actionType, entityType, entityId, oldValues, newValues, notes));
    if (error) { console.warn('Activity log refused:', actionType, error.message); return false; }
    return true;
  } catch (err) {
    // Never let logging break the main operation
    console.warn('Activity log failed:', err);
    return false;
  }
};

// ── Convenience helpers ──

export const logLogin = () =>
  logActivity('login', null, null, null, null, 'User logged in');

export const logLogout = () =>
  logActivity('logout', null, null, null, null, 'User logged out');

export const logLoadCreated = (load) =>
  logLoadActivity(load.id, 'load_created', null, null, [load.customer_name || (load.customer && load.customer.company_name), load.commodity].filter(Boolean).join(', '));

export const logLoadDeleted = (load) =>
  logLoadActivity(load.id, 'load_deleted', 'status', load.status, null, 'Load #' + (load.load_number || '') + ' deleted');

export const logStatusChanged = (load, oldStatus, newStatus) =>
  logLoadActivity(load.id, 'status_changed', 'status', oldStatus, newStatus);

export const logLoadAssigned = (load, driverName, oldDriverName = null) =>
  logLoadActivity(load.id, 'driver_changed', 'driver', oldDriverName, driverName);

export const logLoadUpdated = (load, changes) =>
  logLoadActivity(load.id, 'load_edited', 'fields', null, Array.isArray(changes) ? changes.join(', ') : Object.keys(changes || {}).join(', '));

export const logCustomerCreated = (customer) =>
  logActivity('customer_created', 'customer', customer.id, null, {
    company_name: customer.company_name,
  }, `Customer "${customer.company_name}" created`);

export const logCustomerUpdated = (customer) =>
  logActivity('customer_updated', 'customer', customer.id, null, null,
    `Customer "${customer.company_name}" updated`);

export const logDriverCreated = (driver) =>
  logActivity('driver_created', 'driver', driver.id, null, { name: driver.name },
    `Driver "${driver.name}" created`);

export const logTruckCreated = (truck) =>
  logActivity('truck_created', 'truck', truck.id, null, {
    truck_number: truck.truck_number,
  }, `Truck "${truck.truck_number}" created`);

export const logSettingsChanged = (setting) =>
  logActivity('settings_changed', null, null, null, null, `Settings changed: ${setting}`);

// ── Readers (owner only - RLS) : the real columns, handed back in the shape the window uses ──

const READ_COLS = 'id, dispatcher_user_id, action_type, target_table, related_load_id, related_customer_id, old_value, new_value, details, timestamp';

/** A row in the window's old shape (pure). */
export const toEntry = (r, names = {}) => {
  const d = (r && r.details && typeof r.details === 'object') ? r.details : {};
  return {
    id: r.id,
    user_id: r.dispatcher_user_id,
    action_type: r.action_type,
    entity_type: r.target_table || null,
    entity_id: r.related_load_id || r.related_customer_id || d.entity_id || null,
    old_values: r.old_value,
    new_values: r.new_value,
    notes: d.notes || d.message || (typeof r.details === 'string' ? r.details : null),
    created_at: r.timestamp,
    user: { name: names[r.dispatcher_user_id] || null },
  };
};

const withNames = async (rows) => {
  const ids = [...new Set((rows || []).map((r) => r.dispatcher_user_id).filter(Boolean))];
  const names = {};
  if (ids.length) {
    const { data } = await supabase.from('profiles').select('id, name').in('id', ids);
    (data || []).forEach((p) => { names[p.id] = p.name; });
  }
  return (rows || []).map((r) => toEntry(r, names));
};

/**
 * Fetch activity logs (owner only — RLS enforced)
 */
export const getActivityLog = async (filters = {}) => {
  const { limit = 100, actionType, startDate, endDate, userId } = filters;
  let query = supabase.from('dispatcher_activity_log').select(READ_COLS)
    .order('timestamp', { ascending: false })
    .limit(limit);
  if (actionType) query = query.eq('action_type', actionType);
  if (userId) query = query.eq('dispatcher_user_id', userId);
  if (startDate) query = query.gte('timestamp', startDate);
  if (endDate) query = query.lte('timestamp', endDate);
  const { data, error } = await query;
  if (error) {
    console.error('Error fetching activity log:', error);
    return [];
  }
  return withNames(data);
};

/**
 * Get login/logout history for a specific user
 */
export const getLoginHistory = async (userId = null, days = 30) => {
  const since = new Date();
  since.setDate(since.getDate() - days);
  let query = supabase.from('dispatcher_activity_log').select(READ_COLS)
    .in('action_type', ['login', 'logout'])
    .gte('timestamp', since.toISOString())
    .order('timestamp', { ascending: false })
    .limit(200);
  if (userId) query = query.eq('dispatcher_user_id', userId);
  const { data, error } = await query;
  if (error) {
    console.error('Error fetching login history:', error);
    return [];
  }
  return withNames(data);
};

/**
 * Get activity summary (counts by action type) for a date range
 */
export const getActivitySummary = async (startDate, endDate) => {
  const { data, error } = await supabase
    .from('dispatcher_activity_log')
    .select('action_type, dispatcher_user_id, timestamp')
    .gte('timestamp', startDate)
    .lte('timestamp', endDate);
  if (error || !data) return {};
  const summary = {};
  data.forEach(entry => {
    if (!summary[entry.action_type]) {
      summary[entry.action_type] = 0;
    }
    summary[entry.action_type]++;
  });
  return summary;
};

export default {
  logActivity,
  logLoadActivity,
  logLogin,
  logLogout,
  logLoadCreated,
  logLoadDeleted,
  logStatusChanged,
  logLoadAssigned,
  logLoadUpdated,
  logCustomerCreated,
  logCustomerUpdated,
  logDriverCreated,
  logTruckCreated,
  logSettingsChanged,
  getActivityLog,
  getLoginHistory,
  getActivitySummary,
};
